/**
 * FILE: packages/supabase/src/placements/reads.ts
 * PURPOSE: Placements domain — all read operations and pure helpers.
 * AGENT_CONTEXT: No side effects, no mutations. Safe to call anywhere.
 * INVARIANTS: Soft-delete filter `.is('deleted_at', null)` on all reads.
 * LAST_REVIEWED: 2026-06-10
 */

import type { DbClient } from '@fintree/supabase/db-client'
import { defaultDbClient } from '@fintree/supabase/db-client'
import { ServiceError } from '../internal/errors'
import type { PostgrestFilterBuilder } from '@supabase/supabase-js'

import { computePortalCompletionPercent } from '../candidates/reads'
import type { Database } from '../types'
import type {
  JobWithQualifications,
  JobWithDetails,
  JobFilters,
  ClosedImportJobRow,
  CompanyWithRecruiterCount,
  CompanyWithPOCs,
  RecruiterWithCompany,
  ApplicationStatus,
  ApplicationFilters,
  ApplicationWithDetails,
  ShortlistDraftRow,
  PlacementsInboxTab,
  InboxStaffStatus,
  UrgencyLevel,
  PlacementsInboxItem,
  ShortlistCandidate,
  PortalAnalyticsSnapshot,
  PlacementHistoryRow,
  PlacementHistoryFilters,
} from './dto'

type JobRow = Database['public']['Tables']['jobs']['Row']
type Company = Database['public']['Tables']['companies']['Row']
type Recruiter = Database['public']['Tables']['recruiters']['Row']
type Application = Database['public']['Tables']['applications']['Row']
type Candidate = Database['public']['Tables']['candidates']['Row']
type StudentProfile = Database['public']['Tables']['student_profiles']['Row']
type ClientOptions = Database extends { __InternalSupabase: infer O } ? O : never
type ApplicationQuery = PostgrestFilterBuilder<
  ClientOptions,
  Database['public'],
  Application,
  Application[],
  'applications',
  unknown,
  'GET'
>

/* ════════════════════════════════════════════════════════════════════
   Jobs
   ════════════════════════════════════════════════════════════════════ */

/** Core job qualification codes (Hub multi-select only). */
export const JOB_CORE_QUALIFICATION_CODES = ['CFA_L1', 'CFA_L2', 'CFA_L3', 'FRM_P1', 'FRM_P2', 'Financial_Modeling'] as const

const ALLOWED_CORE = new Set<string>(JOB_CORE_QUALIFICATION_CODES)

/** Curriculum order for sorting selections (lowest index = minimum cleared bar among picks). */
const QUAL_SORT_ORDER: Record<string, number> = {
  CFA_L1: 1,
  CFA_L2: 2,
  CFA_L3: 3,
  FRM_P1: 4,
  FRM_P2: 5,
  Financial_Modeling: 6,
}

/** Split stored `course_required` CSV into trimmed tokens (no filtering). */
export function parseQualifications(courseRequired: string | null | undefined): string[] {
  if (!courseRequired?.trim()) return []
  return courseRequired.split(',').map((s) => s.trim()).filter(Boolean)
}

/** Tokens from `exp_band_required` CSV (e.g. `1-3,5-10`). */
export function parseJobRequiredExpBands(expBandRequired: string | null | undefined): string[] {
  if (!expBandRequired?.trim()) return []
  return expBandRequired.split(',').map((s) => s.trim()).filter(Boolean)
}

/** Tokens from `course_required` restricted to core qualification codes, stable order. */
export function parseJobCoreQualifications(job: Pick<JobRow, 'course_required'>): string[] {
  const raw = parseQualifications(job.course_required)
  return JOB_CORE_QUALIFICATION_CODES.filter((c) => raw.includes(c))
}

export function serializeJobQualifications(qualifications: string[]): {
  course_required: string | null
  min_cleared_level: string | null
} {
  const uniq = [...new Set(qualifications.filter((c) => ALLOWED_CORE.has(c)))]
  if (!uniq.length) return { course_required: null, min_cleared_level: null }
  const sorted = [...uniq].sort((a, b) => (QUAL_SORT_ORDER[a] ?? 99) - (QUAL_SORT_ORDER[b] ?? 99))
  return {
    course_required: sorted.join(','),
    min_cleared_level: sorted[0] ?? null,
  }
}

function withQualifications(row: JobRow): JobWithQualifications {
  return {
    ...row,
    qualifications: parseJobCoreQualifications(row),
  }
}

type CompanyMini = { id: string; name: string; industry: string | null; website: string | null }
type RecruiterMini = { id: string; name: string; email: string | null; designation: string | null }

export async function attachJobDetails(
  rows: JobRow[],
  client: DbClient = defaultDbClient
): Promise<JobWithDetails[]> {
  if (!rows.length) return []
  const withQ = rows.map(withQualifications)
  const cids = [...new Set(rows.map((r) => r.company_id).filter((x): x is string => Boolean(x)))]
  const rids = [...new Set(rows.map((r) => r.recruiter_id).filter((x): x is string => Boolean(x)))]

  const [cRes, rRes] = await Promise.all([
    cids.length ? client.from('companies').select('id,name,industry,website').is('deleted_at', null).in('id', cids) : Promise.resolve({ data: [] as CompanyMini[], error: null }),
    rids.length ? client.from('recruiters').select('id,name,email,designation').is('deleted_at', null).in('id', rids) : Promise.resolve({ data: [] as RecruiterMini[], error: null }),
  ])
  if ('error' in cRes && cRes.error) throw cRes.error
  if ('error' in rRes && rRes.error) throw rRes.error

  const cmap = new Map((cRes.data ?? []).map((c) => [c.id, c]))
  const rmap = new Map((rRes.data ?? []).map((r) => [r.id, r]))

  return withQ.map((row) => {
    const cr = row.company_id ? cmap.get(row.company_id) : undefined
    const rr = row.recruiter_id ? rmap.get(row.recruiter_id) : undefined
    return {
      ...row,
      qualifications: row.qualifications,
      company_record: cr
        ? { id: cr.id, name: cr.name, industry: cr.industry, website: cr.website }
        : null,
      recruiter_record: rr
        ? { id: rr.id, name: rr.name, email: rr.email, designation: rr.designation }
        : null,
    }
  })
}

export async function getJobs(
  filters: JobFilters = {},
  page = 0,
  pageSize = 20,
  client: DbClient = defaultDbClient
): Promise<{ data: JobWithDetails[]; count: number }> {
  const safePage = Math.max(page, 0)
  const safeSize = Math.max(pageSize, 1)
  const from = safePage * safeSize
  const to = from + safeSize - 1

  const sortCol = filters.sortBy === 'posted_date' ? 'posted_date' : 'created_at'
  let query = client
    .from('jobs')
    .select('*', { count: 'exact', head: false })
    .is('deleted_at', null)
    .order(sortCol, { ascending: false })
    .range(from, to)

  if (filters.status?.length) query = query.in('status', filters.status)
  if (typeof filters.visibleToStudents === 'boolean') {
    query = query.eq('visible_to_students', filters.visibleToStudents)
  }
  if (filters.expBandRequired?.length) {
    const parts: string[] = []
    for (const b of filters.expBandRequired) {
      const token = b.trim()
      if (!token) continue
      parts.push(`exp_band_required.eq.${token}`)
      parts.push(`exp_band_required.ilike.%${token}%`)
    }
    if (parts.length) query = query.or(parts.join(','))
  }
  if (filters.courseRequired?.length) query = query.in('course_required', filters.courseRequired)
  if (filters.jobType?.length) query = query.in('job_type', filters.jobType)
  const rid = filters.recruiterId?.trim()
  const cid = filters.companyId?.trim()
  if (rid && cid) {
    query = query.or(`recruiter_id.eq.${rid},company_id.eq.${cid}`)
  } else if (rid) {
    query = query.eq('recruiter_id', rid)
  } else if (cid) {
    query = query.eq('company_id', cid)
  }

  const search = filters.search?.trim()
  if (search) {
    const q = `%${search}%`
    query = query.or(`title.ilike.${q},company.ilike.${q}`)
  }

  const { data, error, count } = await query
  if (error) throw error
  const rows = data ?? []
  const enriched = await attachJobDetails(rows, client)
  return { data: enriched, count: count ?? 0 }
}

export async function getJobById(
  id: string,
  client: DbClient = defaultDbClient
): Promise<JobWithDetails | null> {
  const { data, error } = await client.from('jobs').select('*').eq('id', id).is('deleted_at', null).maybeSingle()
  if (error) throw error
  if (!data) return null
  const [enriched] = await attachJobDetails([data], client)
  return enriched ?? null
}

export async function getClosedImportJobs(
  limit = 200,
  client: DbClient = defaultDbClient
): Promise<ClosedImportJobRow[]> {
  const safeLimit = Math.min(Math.max(limit, 1), 500)
  const { data, error } = await client
    .from('jobs')
    .select('id, title, company, location, posted_date, job_type')
    .is('deleted_at', null)
    .eq('status', 'closed')
    .eq('posted_by', 'import')
    .order('posted_date', { ascending: false })
    .limit(safeLimit)

  if (error) throw error
  return (data ?? []) as ClosedImportJobRow[]
}

export async function getJobDashboardCounts(
  client: DbClient = defaultDbClient
): Promise<{ active: number; filled: number; onHold: number; expired: number }> {
  const now = new Date().toISOString()
  const [activeRes, filledRes, onHoldRes, expiredRes] = await Promise.all([
    client.from('jobs').select('*', { count: 'exact', head: true }).is('deleted_at', null).eq('status', 'active').or(`deadline.is.null,deadline.gte.${now}`),
    client.from('jobs').select('*', { count: 'exact', head: true }).is('deleted_at', null).eq('status', 'filled'),
    client.from('jobs').select('*', { count: 'exact', head: true }).is('deleted_at', null).eq('status', 'on_hold'),
    client.from('jobs').select('*', { count: 'exact', head: true }).is('deleted_at', null).eq('status', 'active').lt('deadline', now),
  ])
  if (activeRes.error) throw activeRes.error
  if (filledRes.error) throw filledRes.error
  if (onHoldRes.error) throw onHoldRes.error
  if (expiredRes.error) throw expiredRes.error
  return {
    active: activeRes.count ?? 0,
    filled: filledRes.count ?? 0,
    onHold: onHoldRes.count ?? 0,
    expired: expiredRes.count ?? 0,
  }
}

/* ════════════════════════════════════════════════════════════════════
   Companies
   ════════════════════════════════════════════════════════════════════ */

export async function getCompanies(
  client: DbClient = defaultDbClient
): Promise<CompanyWithRecruiterCount[]> {
  const { data: companies, error } = await client
    .from('companies')
    .select('*')
    .is('deleted_at', null)
    .or('active.eq.true,active.is.null')
    .order('name', { ascending: true })

  if (error) throw error
  const rows = companies ?? []
  if (!rows.length) return []

  const ids = rows.map((c) => c.id)
  let recruiters: { id: string; company_id: string | null }[] = []
  let companyJobs: { id: string; company_id: string | null; status: string | null }[] = []

  if (ids.length) {
    const [recRes, jobRes] = await Promise.all([
      client
        .from('recruiters')
        .select('id,company_id')
        .in('company_id', ids)
        .or('active.eq.true,active.is.null'),
      client.from('jobs').select('id,company_id,status').is('deleted_at', null).in('company_id', ids),
    ])
    if (recRes.error) throw recRes.error
    if (jobRes.error) throw jobRes.error
    recruiters = recRes.data ?? []
    companyJobs = jobRes.data ?? []
  }

  const countByCompany = new Map<string, number>()
  for (const r of recruiters) {
    if (!r.company_id) continue
    countByCompany.set(r.company_id, (countByCompany.get(r.company_id) ?? 0) + 1)
  }

  const activeJobsByCompany = new Map<string, number>()
  const jobIdsByCompany = new Map<string, string[]>()
  for (const j of companyJobs) {
    if (!j.company_id) continue
    if (j.status === 'active') {
      activeJobsByCompany.set(j.company_id, (activeJobsByCompany.get(j.company_id) ?? 0) + 1)
    }
    const list = jobIdsByCompany.get(j.company_id) ?? []
    list.push(j.id)
    jobIdsByCompany.set(j.company_id, list)
  }

  const allJobIds = [...new Set(companyJobs.map((j) => j.id))]
  const placementsByJob = new Map<string, number>()
  if (allJobIds.length) {
    const { data: placed, error: pe } = await client
      .from('applications')
      .select('job_id')
      .in('job_id', allJobIds)
      .in('status', ['placed', 'selected'])
    if (pe) throw pe
    for (const a of placed ?? []) {
      if (!a.job_id) continue
      placementsByJob.set(a.job_id, (placementsByJob.get(a.job_id) ?? 0) + 1)
    }
  }

  return rows.map((c) => {
    const jids = jobIdsByCompany.get(c.id) ?? []
    let totalPlacements = 0
    for (const jid of jids) {
      totalPlacements += placementsByJob.get(jid) ?? 0
    }
    return {
      ...c,
      recruiterCount: countByCompany.get(c.id) ?? 0,
      activeJobsCount: activeJobsByCompany.get(c.id) ?? 0,
      totalPlacements,
    }
  })
}

export async function searchCompaniesByName(
  query: string,
  limit = 30,
  client: DbClient = defaultDbClient
): Promise<Company[]> {
  const q = query.trim()
  if (!q) {
    const { data, error } = await client
      .from('companies')
      .select('*')
      .is('deleted_at', null)
      .or('active.eq.true,active.is.null')
      .order('name', { ascending: true })
      .limit(limit)
    if (error) throw error
    return data ?? []
  }

  const pattern = `%${q}%`
  const { data, error } = await client
    .from('companies')
    .select('*')
    .is('deleted_at', null)
    .or('active.eq.true,active.is.null')
    .ilike('name', pattern)
    .order('name', { ascending: true })
    .limit(limit)

  if (error) throw error
  return data ?? []
}

export async function getCompanyById(
  id: string,
  client: DbClient = defaultDbClient
): Promise<CompanyWithPOCs | null> {
  const { data: company, error } = await client.from('companies').select('*').eq('id', id).is('deleted_at', null).maybeSingle()
  if (error) throw error
  if (!company) return null

  const [{ data: pocs, error: pe }, { data: jobs, error: je }] = await Promise.all([
    client
      .from('recruiters')
      .select('id,name,email,phone,designation')
      .eq('company_id', id)
      .or('active.eq.true,active.is.null')
      .order('name', { ascending: true }),
    client.from('jobs').select('id,status,fntr_job_id,title').eq('company_id', id).is('deleted_at', null).order('created_at', { ascending: false }),
  ])

  if (pe) throw pe
  if (je) throw je

  const jobRows = jobs ?? []
  const jobIds = jobRows.map((j) => j.id)
  const activeJobsCount = jobRows.filter((j) => j.status === 'active').length

  type PlacedRow = { job_id: string; phone: string | null; candidateName: string | null }

  let placementRows: PlacedRow[] = []

  if (jobIds.length) {
    const { data: placedApps, error: ae } = await client
      .from('applications')
      .select('job_id,phone')
      .in('job_id', jobIds)
      .in('status', ['placed', 'selected'])
    if (ae) throw ae
    const apps = (placedApps ?? []).filter((a) => a.job_id)
    const placedPhones = [...new Set(apps.map((a) => a.phone).filter((p): p is string => Boolean(p)))]
    const { data: candidates } = placedPhones.length
      ? await client.from('candidates').select('phone,name').in('phone', placedPhones)
      : { data: [] }
    const nameByPhone = new Map((candidates ?? []).map((c) => [c.phone ?? '', c.name]))
    placementRows = apps.map((a) => ({
      job_id: a.job_id!,
      phone: a.phone,
      candidateName: a.phone ? nameByPhone.get(a.phone) ?? null : null,
    }))
  }
  const totalPlacements = placementRows.length

  const placementNames: string[] = []
  const seen = new Set<string>()
  for (const row of placementRows) {
    const name = row.candidateName?.trim() || row.phone?.trim() || 'Unknown'
    if (!seen.has(name)) {
      seen.add(name)
      placementNames.push(name)
    }
  }

  return {
    ...company,
    pocs: (pocs ?? []).map((p) => ({
      id: p.id,
      name: p.name,
      email: p.email,
      phone: p.phone,
      designation: p.designation,
    })),
    jobs: (jobRows ?? []).map((j) => ({
      id: j.id,
      fntr_job_id: j.fntr_job_id,
      title: j.title,
      status: j.status,
    })),
    activeJobsCount,
    totalPlacements,
    placementNames,
  }
}

/* ════════════════════════════════════════════════════════════════════
   Recruiters
   ════════════════════════════════════════════════════════════════════ */

type CompanyEmbed = { id: string; name: string; industry: string | null }

function mapRow(
  row: Recruiter & { companies?: CompanyEmbed | CompanyEmbed[] | null }
): RecruiterWithCompany {
  const raw = row.companies
  const emb = Array.isArray(raw) ? raw[0] : raw
  const company = emb ? { id: emb.id, name: emb.name, industry: emb.industry } : null
  const legacy_company = row.company
  const company_label = company?.name ?? legacy_company ?? null

  return {
    id: row.id,
    name: row.name,
    email: row.email,
    phone: row.phone,
    designation: row.designation,
    company_id: row.company_id,
    company,
    legacy_company,
    company_label,
    industry: row.industry,
    notes: row.notes,
    active: row.active !== false && row.active !== null,
    created_at: row.created_at,
  }
}

export async function getRecruiters(
  client: DbClient = defaultDbClient
): Promise<RecruiterWithCompany[]> {
  const { data, error } = await client
    .from('recruiters')
    .select('*, companies(id,name,industry)')
    .or('active.eq.true,active.is.null')

  if (error) throw error
  const rows = (data ?? []) as (Recruiter & { companies?: CompanyEmbed | CompanyEmbed[] | null })[]
  const mapped = rows.map(mapRow)
  const sortKey = (r: RecruiterWithCompany) =>
    `${(r.company_label ?? '\uffff').toLowerCase()}\t${r.name.toLowerCase()}`
  return mapped.sort((a, b) => sortKey(a).localeCompare(sortKey(b)))
}

export async function getRecruitersByCompany(
  companyId: string,
  client: DbClient = defaultDbClient
): Promise<RecruiterWithCompany[]> {
  const { data, error } = await client
    .from('recruiters')
    .select('*, companies(id,name,industry)')
    .eq('company_id', companyId)
    .or('active.eq.true,active.is.null')
    .order('name', { ascending: true })

  if (error) throw error
  const rows = (data ?? []) as (Recruiter & { companies?: CompanyEmbed | CompanyEmbed[] | null })[]
  return rows.map(mapRow)
}

/* ════════════════════════════════════════════════════════════════════
   Applications (shared helpers + reads)
   ════════════════════════════════════════════════════════════════════ */

export function normaliseApplicationStatus(raw: string | null | undefined): ApplicationStatus {
  if (!raw) return 'applied'
  const map: Record<string, ApplicationStatus> = {
    shortlisted: 'shortlisted_fintree',
    sent_to_recruiter: 'forwarded',
    placed: 'selected',
    interview_scheduled: 'interviewed',
    interview_done: 'interviewed',
  }
  return (map[raw] ?? raw) as ApplicationStatus
}

export function isShortlistedDbStatus(s: string | null | undefined): boolean {
  return s === 'shortlisted' || s === 'shortlisted_fintree'
}

export function isPlacedDbStatus(s: string | null | undefined): boolean {
  return s === 'placed' || s === 'selected'
}

/** Internal statuses that end the student-facing application journey. */
export const STUDENT_TERMINAL_STATUSES: ApplicationStatus[] = [
  'rejected',
  'rejected_company',
  'withdrawn',
  'placed',
  'selected',
]

export type ApplicationStatusVariant =
  | 'active'
  | 'inactive'
  | 'expired'
  | 'expiring'
  | 'pending'
  | 'success'
  | 'error'
  | 'warning'

const STATUS_META: Record<ApplicationStatus, { label: string; variant: ApplicationStatusVariant }> = {
  applied: { label: 'Applied', variant: 'pending' },
  reviewed: { label: 'Reviewed', variant: 'warning' },
  shortlisted: { label: 'Shortlisted (FinTree)', variant: 'success' },
  shortlisted_fintree: { label: 'Shortlisted (FinTree)', variant: 'success' },
  forwarded: { label: 'Forwarded', variant: 'success' },
  sent_to_recruiter: { label: 'Forwarded', variant: 'success' },
  shortlisted_company: { label: 'Shortlisted (Company)', variant: 'success' },
  interviewed: { label: 'Interviewed', variant: 'warning' },
  interview_scheduled: { label: 'Interview Scheduled', variant: 'warning' },
  interview_done: { label: 'Interview Done', variant: 'warning' },
  selected: { label: 'Selected', variant: 'success' },
  placed: { label: 'Selected', variant: 'success' },
  rejected_company: { label: 'Rejected (Company)', variant: 'error' },
  rejected: { label: 'Rejected', variant: 'error' },
  withdrawn: { label: 'Withdrawn', variant: 'inactive' },
}

export function getApplicationStatusLabel(status: string | null | undefined): string {
  const key = normaliseApplicationStatus(status)
  return STATUS_META[key]?.label ?? status?.replaceAll('_', ' ') ?? '—'
}

export function getApplicationStatusVariant(status: string | null | undefined): ApplicationStatusVariant {
  const key = normaliseApplicationStatus(status)
  return STATUS_META[key]?.variant ?? 'pending'
}

export const APPLICATION_STATUS_OPTIONS: ApplicationStatus[] = [
  'applied',
  'reviewed',
  'shortlisted',
  'shortlisted_fintree',
  'sent_to_recruiter',
  'forwarded',
  'shortlisted_company',
  'interview_scheduled',
  'interview_done',
  'interviewed',
  'placed',
  'selected',
  'rejected',
  'rejected_company',
  'withdrawn',
]

/** Canonical statuses shown in dropdowns (excludes legacy alias values). */
export const APPLICATION_STATUS_DROPDOWN_OPTIONS: ApplicationStatus[] = [
  'applied',
  'reviewed',
  'shortlisted_fintree',
  'forwarded',
  'shortlisted_company',
  'interviewed',
  'selected',
  'rejected',
  'rejected_company',
  'withdrawn',
]

/** Simplified statuses shown to students and used in admin status filters. */
export type SimplifiedApplicationStatus = 'applied' | 'shortlisted' | 'rejected' | 'withdrawn'

export const SIMPLIFIED_APPLICATION_STATUS_OPTIONS: SimplifiedApplicationStatus[] = [
  'applied',
  'shortlisted',
  'rejected',
  'withdrawn',
]

const SIMPLIFIED_STATUS_LABELS: Record<SimplifiedApplicationStatus, string> = {
  applied: 'Applied',
  shortlisted: 'Shortlisted',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
}

const STATUS_TO_SIMPLIFIED: Record<string, SimplifiedApplicationStatus> = {
  applied: 'applied',
  reviewed: 'applied',
  shortlisted: 'shortlisted',
  shortlisted_fintree: 'shortlisted',
  shortlisted_company: 'shortlisted',
  forwarded: 'shortlisted',
  sent_to_recruiter: 'shortlisted',
  interviewed: 'shortlisted',
  interview_scheduled: 'shortlisted',
  interview_done: 'shortlisted',
  placed: 'shortlisted',
  selected: 'shortlisted',
  rejected: 'rejected',
  rejected_company: 'rejected',
  withdrawn: 'withdrawn',
}

/** Map any internal application status to one of the four simplified statuses. */
export function toSimplifiedApplicationStatus(
  raw: string | null | undefined
): SimplifiedApplicationStatus {
  if (!raw) return 'applied'
  return STATUS_TO_SIMPLIFIED[raw] ?? 'applied'
}

/** Label for a simplified status (Applied / Shortlisted / Rejected / Withdrawn). */
export function getSimplifiedStatusLabel(status: SimplifiedApplicationStatus | string | null | undefined): string {
  const key = toSimplifiedApplicationStatus(status)
  return SIMPLIFIED_STATUS_LABELS[key]
}

/** Expand a simplified status into the internal DB statuses it represents. */
export function simplifiedStatusToInternal(status: SimplifiedApplicationStatus): ApplicationStatus[] {
  switch (status) {
    case 'applied':
      return ['applied', 'reviewed']
    case 'shortlisted':
      return [
        'shortlisted',
        'shortlisted_fintree',
        'shortlisted_company',
        'forwarded',
        'sent_to_recruiter',
        'interviewed',
        'interview_scheduled',
        'interview_done',
        'placed',
        'selected',
      ]
    case 'rejected':
      return ['rejected', 'rejected_company']
    case 'withdrawn':
      return ['withdrawn']
    default:
      return []
  }
}

const APPLICATIONS_LIST_LIMIT = 2000
const APPLICATIONS_EXPORT_PAGE_SIZE = 500
const APPLICATIONS_EXPORT_MAX_PAGES = 500

function applyApplicationFilters(query: ApplicationQuery, filters: ApplicationFilters) {
  let next = query.is('deleted_at', null)
  if (filters.jobId) next = next.eq('job_id', filters.jobId)
  if (filters.status?.length) next = next.in('status', filters.status)
  if (filters.matchLevel?.length) next = next.in('match_level', filters.matchLevel)
  if (filters.phone) next = next.eq('phone', filters.phone)
  if (filters.dateFrom) next = next.gte('created_at', filters.dateFrom)
  if (filters.dateTo) next = next.lte('created_at', filters.dateTo)
  return next
}

async function enrichApplications(
  rows: Application[],
  client: DbClient = defaultDbClient
): Promise<ApplicationWithDetails[]> {
  if (!rows.length) return []

  const jobIds = [...new Set(rows.map((r) => r.job_id).filter((id): id is string => Boolean(id)))]
  const phones = [...new Set(rows.map((r) => r.phone).filter((p): p is string => Boolean(p)))]

  const jobsRes =
    jobIds.length > 0
      ? await client.from('jobs').select('id,title,company,fntr_job_id').is('deleted_at', null).in('id', jobIds)
      : { data: [] as { id: string; title: string; company: string | null; fntr_job_id: string | null }[], error: null }
  const studentsRes =
    phones.length > 0
      ? await client.from('students').select('phone,name,course,city,email').in('phone', phones).is('deleted_at', null)
      : { data: [] as { phone: string; name: string | null; course: string | null; city: string | null; email: string | null }[], error: null }
  const candidatesRes =
    phones.length > 0
      ? await client
          .from('candidates')
          .select(
            'phone,name,exp_band,resume_url,current_position,experience_years,education,skills,linkedin_url'
          )
          .in('phone', phones)
      : {
          data: [] as Pick<
            Candidate,
            | 'phone'
            | 'name'
            | 'exp_band'
            | 'resume_url'
            | 'current_position'
            | 'experience_years'
            | 'education'
            | 'skills'
            | 'linkedin_url'
          >[],
          error: null,
        }
  const profilesRes =
    phones.length > 0
      ? await client
          .from('student_profiles')
          .select('phone,company,role,city,graduation_year,linkedin_url,avatar_url,exp_band')
          .in('phone', phones)
      : { data: [] as Pick<StudentProfile, 'phone' | 'company' | 'role' | 'city' | 'graduation_year' | 'linkedin_url' | 'avatar_url' | 'exp_band'>[], error: null }

  if (jobsRes.error) throw jobsRes.error
  if (studentsRes.error) throw studentsRes.error
  if (candidatesRes.error) throw candidatesRes.error
  if (profilesRes.error) throw profilesRes.error

  const jobMap = new Map((jobsRes.data ?? []).map((j) => [j.id, j]))
  const studentMap = new Map((studentsRes.data ?? []).map((s) => [s.phone, s]))
  const candidateMap = new Map((candidatesRes.data ?? []).map((c) => [c.phone, c]))
  const profileMap = new Map((profilesRes.data ?? []).map((p) => [p.phone, p]))

  return rows.map((row) => {
    if (!row.job_id) throw new Error('Application is missing job_id')
    const j = jobMap.get(row.job_id)
    const s = row.phone ? studentMap.get(row.phone) : undefined
    const c = row.phone ? candidateMap.get(row.phone) : undefined
    const p = row.phone ? profileMap.get(row.phone) : undefined
    const hasCandidateSummary = Boolean(c || p)
    return {
      ...row,
      job: j ? { title: j.title, company: j.company, fntr_job_id: j.fntr_job_id } : undefined,
      student: s ? { name: s.name, course: s.course, city: s.city, email: s.email } : undefined,
      candidate: hasCandidateSummary
        ? {
            name: c?.name ?? s?.name ?? null,
            exp_band: c?.exp_band ?? p?.exp_band ?? null,
            resume_url: c?.resume_url ?? null,
            current_position: c?.current_position ?? null,
            experience_years: c?.experience_years ?? null,
            education: c?.education ?? null,
            skills: c?.skills ?? null,
            linkedin_url: c?.linkedin_url ?? null,
            portal_company: p?.company ?? null,
            portal_role: p?.role ?? null,
            portal_city: p?.city ?? null,
            portal_graduation_year: p?.graduation_year ?? null,
            portal_linkedin_url: p?.linkedin_url ?? null,
            portal_avatar_url: p?.avatar_url ?? null,
            portal_exp_band: p?.exp_band ?? null,
            portal_completion_percent: computePortalCompletionPercent(p ?? null, c ?? null, {
              city: s?.city ?? null,
            }),
          }
        : undefined,
    }
  })
}

/** Lists applications with optional filters; joins job, student, and candidate summaries. */
export async function getApplications(
  filters: ApplicationFilters = {},
  client: DbClient = defaultDbClient
): Promise<ApplicationWithDetails[]> {
  const query = applyApplicationFilters(
    client.from('applications').select('*').is('deleted_at', null).order('created_at', { ascending: false }).limit(APPLICATIONS_LIST_LIMIT),
    filters
  )

  const { data, error } = await query
  if (error) throw error
  return enrichApplications(data ?? [], client)
}

/** Cursor-paginated export — fetches all matching applications without offset loops (H8). */
export async function fetchAllApplicationsForExport(
  filters: ApplicationFilters = {},
  client: DbClient = defaultDbClient
): Promise<ApplicationWithDetails[]> {
  const rows: Application[] = []
  let cursor: { created_at: string; id: string } | null = null
  let prevCursorKey: string | null = null

  for (let page = 0; page < APPLICATIONS_EXPORT_MAX_PAGES; page += 1) {
    let query = applyApplicationFilters(
      client
        .from('applications')
        .select('*')
        .order('created_at', { ascending: false })
        .order('id', { ascending: true })
        .limit(APPLICATIONS_EXPORT_PAGE_SIZE),
      filters
    )

    if (cursor) {
      query = query.or(`created_at.lt.${cursor.created_at},and(created_at.eq.${cursor.created_at},id.gt.${cursor.id})`)
    }

    const { data, error } = await query
    if (error) throw error
    if (!data?.length) break

    rows.push(...data)
    if (data.length < APPLICATIONS_EXPORT_PAGE_SIZE) break

    const last = data[data.length - 1]
    if (!last?.created_at || !last.id) break
    const nextCursorKey = `${last.created_at}:${last.id}`
    if (nextCursorKey === prevCursorKey) break
    prevCursorKey = nextCursorKey
    cursor = { created_at: last.created_at, id: last.id }
  }

  return enrichApplications(rows, client)
}

/** Applications for a single job (newest first), with joined details. */
export async function getApplicationsForJob(
  jobId: string,
  client: DbClient = defaultDbClient
): Promise<ApplicationWithDetails[]> {
  return getApplications({ jobId }, client)
}

/** Per-job application totals and shortlisted counts for dashboard cards. */
export async function countApplicationsByJobIds(
  jobIds: string[],
  client: DbClient = defaultDbClient
): Promise<Record<string, { total: number; shortlisted: number; unreviewed: number }>> {
  const out: Record<string, { total: number; shortlisted: number; unreviewed: number }> = {}
  if (!jobIds.length) return out
  const { data, error } = await client.from('applications').select('job_id,status,phone').is('deleted_at', null).in('job_id', jobIds)
  if (error) throw error
  const rows = data ?? []
  for (const row of rows) {
    const jid = row.job_id
    if (!jid) continue
    if (!out[jid]) out[jid] = { total: 0, shortlisted: 0, unreviewed: 0 }
    out[jid].total += 1
    if (isShortlistedDbStatus(row.status)) out[jid].shortlisted += 1
    if ((row.status ?? 'applied') === 'applied') out[jid].unreviewed += 1
  }
  return out
}

/* ════════════════════════════════════════════════════════════════════
   Placements Inbox
   ════════════════════════════════════════════════════════════════════ */

function startOfLocalDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

export function computeUrgency(iso: string | null | undefined): UrgencyLevel {
  if (!iso) return 'on_track'
  const due = new Date(iso)
  const now = new Date()
  const endToday = new Date(startOfLocalDay(now))
  endToday.setHours(23, 59, 59, 999)
  if (due.getTime() <= endToday.getTime()) return 'today'
  const msLeft = due.getTime() - now.getTime()
  const weekMs = 7 * 24 * 60 * 60 * 1000
  if (msLeft > 0 && msLeft <= weekMs) return 'week'
  if (msLeft <= 0) return 'today'
  return 'on_track'
}

function pipelineTabFromStatus(raw: string | null): PlacementsInboxTab {
  const s = normaliseApplicationStatus(raw)
  if (s === 'withdrawn') return 'followups'
  if (s === 'rejected' || s === 'rejected_company') return 'rejections'
  if (s === 'selected') return 'offers'
  if (
    s === 'interviewed' ||
    s === 'interview_scheduled' ||
    s === 'interview_done' ||
    s === 'shortlisted_company'
  ) {
    return 'interviews'
  }
  return 'applications'
}

function inboxStaffNeedsTouch(inbox: Pick<InboxRow, 'staff_status' | 'follow_up_due'> | null): boolean {
  if (!inbox) return false
  if (inbox.staff_status === 'send_update' || inbox.staff_status === 'scheduled') return true
  if (inbox.follow_up_due) {
    const u = computeUrgency(inbox.follow_up_due)
    return u === 'today' || u === 'week'
  }
  return false
}

export interface PlacementsInboxPaginatedResult {
  items: PlacementsInboxItem[]
  total: number
}

interface PlacementsInboxRpcRow {
  id: string
  job_id: string
  phone?: string | null
  candidate_name?: string | null
  job_title?: string | null
  job_company?: string | null
  status: string
  staff_status?: InboxStaffStatus | null
  follow_up_due?: string | null
  interview_date?: string | null
  updated_at: string | null
  created_at: string | null
}

export async function getPlacementsInboxPaginated(
  page = 0,
  pageSize = 50,
  tab = 'all',
  search?: string,
  client: DbClient = defaultDbClient
): Promise<PlacementsInboxPaginatedResult> {
  const { data, error } = await client.rpc('get_placements_inbox', {
    p_page: page,
    p_page_size: pageSize,
    p_tab: tab,
    p_search: search || undefined,
  })
  if (error) throw new ServiceError(error.message, 'PLACEMENTS_INBOX_RPC', error)

  const raw = (data as { rows: PlacementsInboxRpcRow[]; total: number } | null) ?? { rows: [], total: 0 }

  const items: PlacementsInboxItem[] = raw.rows.map((r) => {
    const pipelineTab = pipelineTabFromStatus(r.status)
    const staffStatus = r.staff_status ?? null
    const followUpDue = r.follow_up_due ?? null
    const interviewDate = r.interview_date ?? null
    const refDate = followUpDue ?? interviewDate
    const urgency = computeUrgency(refDate)
    const needsStaffFollowUp = inboxStaffNeedsTouch({ staff_status: staffStatus, follow_up_due: followUpDue })
    return {
      applicationId: r.id,
      jobId: r.job_id,
      studentPhone: r.phone ?? '',
      studentName: r.candidate_name ?? 'Unknown',
      jobTitle: r.job_title ?? '—',
      company: r.job_company ?? null,
      applicationStatus: r.status,
      pipelineTab,
      staffStatus,
      followUpDue,
      lastUpdate: r.updated_at ?? r.created_at,
      interviewDate,
      urgency,
      needsStaffFollowUp,
    }
  })

  return { items, total: raw.total }
}

export interface ApplicationsDashboardPaginatedResult {
  rows: ApplicationWithDetails[]
  total: number
}

interface ApplicationsDashboardRpcRow {
  id: string
  phone?: string | null
  job_id: string
  status: string
  match_level: string | null
  created_at: string | null
  updated_at: string | null
  notes: string | null
  interview_date: string | null
  job_title?: string | null
  job_company?: string | null
  candidate_name?: string | null
  student_course?: string | null
  student_email?: string | null
}

export async function getApplicationsDashboardPaginated(
  page = 0,
  pageSize = 50,
  status?: string[],
  dateFrom?: string,
  dateTo?: string,
  jobId?: string,
  client: DbClient = defaultDbClient
): Promise<ApplicationsDashboardPaginatedResult> {
  const { data, error } = await client.rpc('get_applications_dashboard', {
    p_page: page,
    p_page_size: pageSize,
    p_status: status || undefined,
    p_date_from: dateFrom || undefined,
    p_date_to: dateTo || undefined,
    p_job_id: jobId || undefined,
  })
  if (error) throw new ServiceError(error.message, 'APPLICATIONS_DASHBOARD_RPC', error)

  const raw = (data as { rows: ApplicationsDashboardRpcRow[]; total: number } | null) ?? { rows: [], total: 0 }

  const rows: ApplicationWithDetails[] = raw.rows.map((r) => ({
    id: r.id,
    phone: r.phone ?? '',
    job_id: r.job_id,
    status: r.status,
    match_level: r.match_level,
    created_at: r.created_at,
    updated_at: r.updated_at,
    notes: r.notes ?? null,
    placed: r.status === 'placed',
    applied_via: null,
    deleted_at: null,
    interview_date: r.interview_date ?? null,
    package_lpa: null,
    recruiter_feedback: null,
    rejection_reason: null,
    sent_to_recruiter_at: null,
    shortlisted_at: null,
    shortlisted_by: null,
    withdrawn_at: null,
    job: r.job_title
      ? { title: r.job_title, company: r.job_company ?? null, fntr_job_id: null }
      : undefined,
    student: r.candidate_name
      ? { name: r.candidate_name, course: r.student_course ?? null, city: null, email: r.student_email ?? null }
      : undefined,
    candidate: undefined,
  }))

  return { rows, total: raw.total }
}

export interface PlacementInboxCount {
  status: string
  count: number
  latestAt: string | null
}

type InboxRow = {
  application_id: string
  staff_status: InboxStaffStatus | null
  follow_up_due: string | null
  updated_at: string
}

/* ════════════════════════════════════════════════════════════════════
   Shortlist helpers (pure, no mutations)
   ════════════════════════════════════════════════════════════════════ */

export function buildShortlistEmailDraft(
  job: { title: string; company: string | null; fntr_job_id: string | null },
  recruiter: { name: string },
  rows: ShortlistDraftRow[]
): string {
  const company = job.company ?? '—'
  const blocks = rows.map((r, i) => {
    const expY = r.experience_years != null ? `${r.experience_years} years` : '—'
    const band = r.exp_band ?? '—'
    return `${i + 1}. ${r.name}\n   Current Role: ${r.current_position ?? '—'}\n   Experience: ${expY} (${band})\n   Education: ${r.education ?? '—'}\n   Skills: ${r.skills ?? '—'}\n   LinkedIn: ${r.linkedin_url?.trim() ? r.linkedin_url : 'Not provided'}\n   Resume: ${r.resume_url?.trim() ? r.resume_url : 'Not provided'}`
  })

  return `Subject: Shortlisted Candidates — ${job.title} (${job.fntr_job_id ?? '—'})

Dear ${recruiter.name},

Please find below the shortlisted candidates for the ${job.title} position at ${company}.

---

${blocks.join('\n\n')}

---

Please review the profiles and let us know your feedback.

Best regards,
FinTree Placements Team`
}

export function buildShortlistEmail(
  jobTitle: string,
  company: string,
  candidates: ShortlistCandidate[]
): string {
  const rows = candidates.map((c, i) =>
    `${i + 1}. ${c.name} | ${c.phone} | ${c.course} | ${c.experience} | ${c.matchLevel}${c.notes ? ` | Notes: ${c.notes}` : ''}`
  ).join('\n')

  return `Dear Hiring Manager,

Please find below the shortlisted candidates for the position of ${jobTitle} at ${company}:

${rows}

All candidate resumes are attached in the zip file.

Please let us know if you need any additional information.

Best regards,
FinTree Placements Team`
}

/* ════════════════════════════════════════════════════════════════════
   Analytics
   ════════════════════════════════════════════════════════════════════ */

export async function getPortalAnalyticsSnapshot(
  periodDays?: number,
  client: DbClient = defaultDbClient
): Promise<PortalAnalyticsSnapshot> {
  const { data, error } = await client.rpc('get_portal_analytics_snapshot', {
    p_days: periodDays ?? undefined,
  })
  if (error) throw new ServiceError('Failed to load portal analytics', 'PORTAL_ANALYTICS_ERROR', error)
  return (data ?? {}) as unknown as PortalAnalyticsSnapshot
}
/* ════════════════════════════════════════════════════════════════════
   Placement History
   ════════════════════════════════════════════════════════════════════ */

export async function getPlacementHistory(
  filters: PlacementHistoryFilters = {},
  client: DbClient = defaultDbClient
): Promise<PlacementHistoryRow[]> {
  let query = client
    .from('placement_history')
    .select('id, student_name, company_name, role, salary, placed_date, source, created_at')
    .order('placed_date', { ascending: false })

  const search = filters.search?.trim()
  if (search) {
    const q = `%${search}%`
    query = query.or(`student_name.ilike.${q},company_name.ilike.${q},role.ilike.${q}`)
  }

  const company = filters.company?.trim()
  if (company) {
    query = query.ilike('company_name', `%${company}%`)
  }

  if (filters.year) {
    query = query.gte('placed_date', `${filters.year}-01-01`).lte('placed_date', `${filters.year}-12-31`)
  }

  const { data, error } = await query
  if (error) throw error

  return (data ?? []).map((row) => ({
    id: row.id,
    studentName: row.student_name,
    companyName: row.company_name,
    role: row.role,
    salary: row.salary,
    placedDate: row.placed_date,
    source: row.source,
    createdAt: row.created_at,
  })) as PlacementHistoryRow[]
}
